import { useMemo, useState } from 'react';

import { ItemType } from '../../../@types/python.d';
import { useAuth } from '../../context/Authentication.context';
import { FetchItemTypesResponse, useFetchItemTypesQuery } from './python.hooks';

export interface UseItemResultHookParams {
	initialFetch?: boolean;
}

export interface UseItemTypesHook {
	itemTypes: ItemType[];
	isLoading: boolean;
	getItemType: (itemTypeId: string) => ItemType | undefined;
	getDefaultItemType: () => ItemType;
	fetchItemTypes: () => Promise<FetchItemTypesResponse | undefined>;
}

export const getItemTypesFromUser = () => {
	return window.global.central.user().group.itemTypes ?? [];
};

const useItemTypes: (params?: UseItemResultHookParams) => UseItemTypesHook = (params) => {
	const auth = useAuth();
	const groupId = auth.workspace.id;
	const [_itemTypes, setItemTypes] = useState(getItemTypesFromUser());

	const { isLoading, refetch: refetchItemTypes } = useFetchItemTypesQuery(
		{ groupId },
		{
			enabled: params?.initialFetch ?? false,
			keepPreviousData: true,
			select: (data) => {
				// Sort alphabetically but keep the default on top
				return data.sort((a, b) => {
					if (a.is_default) return -1;
					return a.name.localeCompare(b.name);
				});
			},
			onSuccess: (data) => {
				setItemTypes(data);
			},
		}
	);

	// We can quickly grab the item types from the user object's group
	// but this API call will not include the item count
	const itemTypes = useMemo(() => {
		const itemTypes = _itemTypes;
		// Sort alphabetically but keep the default on top
		return itemTypes.sort((a, b) => {
			if (a.is_default) {
				return -1;
			}

			return a.name.localeCompare(b.name);
		});
	}, [_itemTypes]);

	/**
	 * This will fetch the item types from the server and include the item count
	 */
	const fetchItemTypes = async () => {
		const { data } = await refetchItemTypes();
		await window.global.central.loadUser(true); // Refresh the user object in legacy
		return data;
	};

	const getItemType = (itemTypeId: string) => {
		return itemTypes.find((itemType) => itemType.id === itemTypeId);
	};

	const getDefaultItemType = () => {
		return itemTypes.find((itemType) => itemType.is_default)!;
	};

	return {
		itemTypes,
		isLoading,
		fetchItemTypes,
		getItemType,
		getDefaultItemType,
	};
};

export default useItemTypes;
